import React from 'react';

interface KeyboardShortcutsOverlayProps {
  open: boolean;
  onClose: () => void;
}

const shortcuts = [
  {
    section: 'Global',
    items: [
      { keys: ['Cmd', 'K'], description: 'Open command palette' },
      { keys: ['Cmd', '1'], description: 'Focus Email panel' },
      { keys: ['Cmd', '2'], description: 'Focus Todo panel' },
      { keys: ['Cmd', '3'], description: 'Focus Notes panel' },
      { keys: ['Cmd', 'N'], description: 'New item (context-aware)' },
      { keys: ['C'], description: 'Compose new email' },
      { keys: ['Esc'], description: 'Close modal / back / collapse' },
      { keys: ['?'], description: 'Show keyboard shortcuts' },
    ],
  },
  {
    section: 'Email Panel',
    items: [
      { keys: ['j', 'Down'], description: 'Next thread' },
      { keys: ['k', 'Up'], description: 'Previous thread' },
      { keys: ['Enter', 'o'], description: 'Open thread' },
      { keys: ['r'], description: 'Reply' },
      { keys: ['a'], description: 'Reply all' },
      { keys: ['f'], description: 'Forward' },
      { keys: ['e'], description: 'Archive' },
      { keys: ['s'], description: 'Snooze' },
      { keys: ['x'], description: 'Star / unstar' },
      { keys: ['#'], description: 'Delete' },
      { keys: ['u'], description: 'Mark unread' },
      { keys: ['Shift', 'I'], description: 'Toggle read/unread' },
    ],
  },
  {
    section: 'Todo Panel',
    items: [
      { keys: ['n'], description: 'New task (focus quick-add)' },
      { keys: ['j', 'Down'], description: 'Next task' },
      { keys: ['k', 'Up'], description: 'Previous task' },
      { keys: ['Enter'], description: 'Edit task inline' },
      { keys: ['Space'], description: 'Toggle complete' },
      { keys: ['1-4'], description: 'Set priority (P1-P4)' },
      { keys: ['d'], description: 'Set due date' },
      { keys: ['Tab'], description: 'Indent (subtask)' },
      { keys: ['Shift', 'Tab'], description: 'Outdent' },
    ],
  },
  {
    section: 'Notes Panel',
    items: [
      { keys: ['Cmd', 'N'], description: 'New note' },
      { keys: ['j', 'Down'], description: 'Next note' },
      { keys: ['k', 'Up'], description: 'Previous note' },
      { keys: ['Enter'], description: 'Open note' },
      { keys: ['/'], description: 'Slash command (in editor)' },
      { keys: ['[['], description: 'Link autocomplete' },
      { keys: ['Cmd', 'S'], description: 'Force save' },
    ],
  },
];

export function KeyboardShortcutsOverlay({
  open,
  onClose,
}: KeyboardShortcutsOverlayProps) {
  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ backgroundColor: 'var(--bg-overlay)' }}
      onClick={onClose}
    >
      <div
        className="w-full max-w-lg max-h-[80vh] overflow-y-auto rounded-lg"
        style={{ backgroundColor: 'var(--bg-surface)', boxShadow: 'var(--shadow-lg)' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          className="sticky top-0 flex items-center justify-between px-6 py-4 border-b"
          style={{
            backgroundColor: 'var(--bg-surface)',
            borderColor: 'var(--border)',
          }}
        >
          <h2 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>
            Keyboard Shortcuts
          </h2>
          <button
            type="button"
            aria-label="Close keyboard shortcuts"
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded text-sm font-mono transition-colors"
            style={{
              color: 'var(--text-tertiary)',
              backgroundColor: 'transparent',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--bg-hover)';
              e.currentTarget.style.color = 'var(--text-primary)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
              e.currentTarget.style.color = 'var(--text-tertiary)';
            }}
          >
            <svg
              aria-hidden="true"
              viewBox="0 0 24 24"
              className="h-4 w-4"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
            >
              <path d="M6 6l12 12M18 6L6 18" />
            </svg>
          </button>
        </div>

        {/* Shortcuts */}
        <div className="px-6 py-4 space-y-6">
          {shortcuts.map((group) => (
            <div key={group.section}>
              <h3
                className="text-xs font-mono uppercase tracking-wider mb-3"
                style={{ color: 'var(--text-tertiary)' }}
              >
                {group.section}
              </h3>
              <div className="space-y-2">
                {group.items.map((item) => (
                  <div
                    key={item.description}
                    className="flex items-center justify-between py-1"
                  >
                    <span
                      className="text-sm"
                      style={{ color: 'var(--text-secondary)' }}
                    >
                      {item.description}
                    </span>
                    <div className="flex items-center gap-1">
                      {item.keys.map((key, i) => (
                        <React.Fragment key={i}>
                          {i > 0 && (
                            <span
                              className="text-xs font-mono"
                              style={{ color: 'var(--text-tertiary)' }}
                            >
                              +
                            </span>
                          )}
                          <kbd
                            className="px-2 py-0.5 text-xs font-mono rounded"
                            style={{
                              backgroundColor: 'var(--bg-elevated)',
                              color: 'var(--text-primary)',
                              border: '1px solid var(--border-strong)',
                            }}
                          >
                            {key}
                          </kbd>
                        </React.Fragment>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Footer hint */}
        <div
          className="px-6 py-3 border-t text-center text-xs font-mono"
          style={{ color: 'var(--text-tertiary)', borderColor: 'var(--border)' }}
        >
          Press <kbd className="px-1 rounded" style={{ backgroundColor: 'var(--bg-elevated)' }}>?</kbd> or{' '}
          <kbd className="px-1 rounded" style={{ backgroundColor: 'var(--bg-elevated)' }}>Esc</kbd> to close
        </div>
      </div>
    </div>
  );
}
